import {defineStore} from 'pinia'
import {useAuthApiStore} from '@toolify/client/src/stores/api/AuthApiStore/useAuthApiStore'

import {useMeApiStore} from '@toolify/client/src/stores/api/MeApiStore/useMeApiStore'
import {setItem, getItem, removeItem} from 'localforage'
import {$ref, $$, $computed} from '@vue-macros/reactivity-transform/macros'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {IUserUpdatedEmitPayload} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IUserUpdatedEmitPayload'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {mapUserResponseInstance} from '@toolify/client/src/modules/api/singletons/mapUserResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {
  IResendTokenEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResendTokenEmitPayload'
import {useLocalPreferenceStore} from '../LocalPreferenceStore/useLocalPreferenceStore'
import {LocalStorageVariablesType} from '../../types/LocalStorageVariablesType'
import {LocalStorageKeyType} from '@toolify/client/src/types/LocalStorageKeyType'
import {i18n} from '@toolify/client/src/setup/i18n'
import {useMobileNotificationsStore} from '../MobileNotificationsStore/useMobileNotificationsStore'
import {watch} from 'vue'
import {useCurrentSessionApiStore} from '../api/CurrentSessionApiStore/useCurrentSessionApiStore'
import {useCurrentAppPlatform} from '../../modules/platform/composables/useCurrentAppPlatform'
import {useOperatingSystem} from '../../modules/app/composables/useOperatingSystem'
import {useOperatingSystemData} from '../../modules/app/composables/useOperatingSystemData'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {OperatingSystemType} from '@toolify/server/src/controllers/NativeAppController/enum/OperatingSystemType'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {isGodModeEnabled} from '@toolify/client/src/stores/AdminRuntimeStore/functions/isGodModeEnabled'
import {
  IDatabaseTimeTrackerItemResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabaseTimeTrackerItemResponseInstance'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'

export const useAuthStore = defineStore('auth', () => {
  let isLoggedIn = $ref(false)
  let currentEntity = $ref<IResponseEntity>(null)
  let lastFetchedToken: string | null = null
  let isRenewingToken = $ref(false)
  const currentLinkId = $ref(null)
  const {t} = i18n.global
  const messages = useMessages()

  const TOKEN_RENEWAL_INITIAL_RETRY_DELAY_MS = 1000
  const TOKEN_RENEWAL_MAX_RETRY_DELAY_MS = 30000

  // the token sync runs from several watchers, so one failure can be reported more than once
  function showDeviceTokenError() {
    messages.error(t('mobile.notifications.deviceTokenError'), {
      collapseKey: 'mobile.notifications.deviceTokenError',
    })
  }

  // mirrors the server constant AuthTokenExpiresOnServerInMinutes: the api rejects a token this long after it was issued,
  // no matter how far in the future the expiresAt claim itself is
  const AUTH_TOKEN_EFFECTIVE_LIFETIME_MINUTES = 6
  const AUTH_TOKEN_RENEWAL_MARGIN_SECONDS = 60

  let renewTokenPromise: Promise<void> = null

  const socketStore = useSocketStore()
  const mobileNotificationsStore = useMobileNotificationsStore()
  const currentSessionApiStore = useCurrentSessionApiStore()
  const {currentAppPlatformType} = useCurrentAppPlatform()
  const {getOperatingSystem} = useOperatingSystem()
  const {getOperatingSystemData} = useOperatingSystemData()

  function initialize() {
    socketStore.socket.on(GlobalSocketEventType.UserUpdated, onUserUpdated)
  }

  function dispose() {
    socketStore.socket.off(GlobalSocketEventType.UserUpdated, onUserUpdated)
  }

  watch(() => mobileNotificationsStore.currentDeviceToken, async (value) => {
    if(!value || !isLoggedIn) {
      return
    }
    try {
      await currentSessionApiStore.updateOperatingSystemData({
        deviceToken: value,
      })
    } catch (error) {
      showDeviceTokenError()
      // eslint-disable-next-line no-console
      console.error(error)
    }
  })

  watch(() => isLoggedIn, async (value) => {
    if(!value) {
      return
    }
    if(!mobileNotificationsStore.currentDeviceToken) {
      await mobileNotificationsStore.checkNotificationPermission()
      return 
    }
    try {
      await currentSessionApiStore.updateOperatingSystemData({
        deviceToken: mobileNotificationsStore.currentDeviceToken,
      })
    } catch (error) {
      showDeviceTokenError()
      // eslint-disable-next-line no-console
      console.error(error)
    }
  })

  function onUserUpdated(payload: IToJson<IUserUpdatedEmitPayload>) {
    if(!currentEntity || currentEntity.entityType !== EntityType.User) {
      return
    }
    if(payload.userId === currentEntity.entityId) {
      currentEntity = {
        ...currentEntity,
        payload: {
          ...currentEntity.payload,
          ...mapUserResponseInstance(payload.user),
        },
      }
    }
  }
  const renewToken = async (): Promise<void> => {
    if(renewTokenPromise) {
      return await renewTokenPromise
    }
    renewTokenPromise = renewTokenUntilSettled()
    try {
      await renewTokenPromise
    } finally {
      renewTokenPromise = null
    }
  }

  const renewTokenUntilSettled = async (): Promise<void> => {
    isRenewingToken = true
    try {
      let attempt = 0
      while(await getItem('auth-token')) {
        const retryDelayMs = await requestTokenRenewal(attempt)
        if(retryDelayMs === null) {
          return
        }
        attempt++
        await new Promise(resolve => setTimeout(resolve, retryDelayMs))
      }
    } finally {
      isRenewingToken = false
    }
  }

  const requestTokenRenewal = async (attempt: number): Promise<number | null> => {
    const authApiStore = useAuthApiStore()
    let response: Awaited<ReturnType<typeof authApiStore.refreshToken>>
    try {
      response = await authApiStore.refreshToken()
    } catch(error) {
      return getTokenRenewalRetryDelayMs(error, attempt)
    }
    const emitPayload: IResendTokenEmitPayload = {
      token: response.token,
      godMode: isGodModeEnabled(),
    }
    socketStore.socket.emit(GlobalSocketEventType.ResendToken, emitPayload)
    await setTokenWithExpiryDate(response.token, response.expiresAt)
    refreshCurrentEntityAfterTokenRenewal()
    return null
  }

  const refreshCurrentEntityAfterTokenRenewal = async (): Promise<void> => {
    const entity = await fetchCurrentEntity().catch(() => null)
    if(!entity) {
      return
    }
    currentEntity = entity
    isLoggedIn = entity.entityType === EntityType.User
  }

  const getTokenRenewalRetryDelayMs = (error: unknown, attempt: number): number | null => {
    if(error instanceof ApiException && [KnownError.INVALID_TOKEN, KnownError.MISSING_TOKEN].includes(error.name)) {
      return null
    }
    const backoffDelayMs = Math.min(TOKEN_RENEWAL_INITIAL_RETRY_DELAY_MS * 2 ** attempt, TOKEN_RENEWAL_MAX_RETRY_DELAY_MS)
    const retryAfterSeconds = error instanceof ApiException ? Number(error.extra?.retryAfterSeconds) : NaN
    const delayMs = Number.isFinite(retryAfterSeconds) ? Math.max(retryAfterSeconds * 1000, backoffDelayMs) : backoffDelayMs
    return delayMs + Math.random() * TOKEN_RENEWAL_INITIAL_RETRY_DELAY_MS
  }

  const renewTokenIfNeeded = async (): Promise<void> => {
    if(!await isTokenRenewalNeeded()) {
      return
    }
    await renewToken()
  }
  const fetchCurrentEntity = async () => {
    const meApiStore = useMeApiStore()
    const {user} = await meApiStore.me()
    return user
  }

  async function createSession() {
    const authApiStore = useAuthApiStore()
    const osType = getOperatingSystem() || OperatingSystemType.Windows
    const osData = getOperatingSystemData()

    const response = await authApiStore.createSession(
      currentAppPlatformType,
      osType,
      osData,
    )
    await setTokenWithExpiryDate(response.token, response.expiresAt)
    await refetchCurrentEntity()
  }

  async function initializeAuth() {
    const token = await getItem('auth-token')
    if(!token) {
      currentEntity = null
      isLoggedIn = false
      lastFetchedToken = null

      try {
        await createSession()
      } catch (e) {
        // console.error('Failed to init guest session', e)
      }
      return
    }
    if(currentEntity && lastFetchedToken === token) {
      return
    }
    await refetchCurrentEntity()
  }

  const refetchCurrentEntity = async () => {
    const token = await getItem<string>('auth-token')
    const entity = await fetchCurrentEntity()
    currentEntity = entity
    isLoggedIn = entity.entityType === EntityType.User
    lastFetchedToken = token

    // Try to send device token if it's already available
    if (mobileNotificationsStore.currentDeviceToken && isLoggedIn) {
      try {
        await currentSessionApiStore.updateOperatingSystemData({
          deviceToken: mobileNotificationsStore.currentDeviceToken,
        })
      } catch (error) {
        showDeviceTokenError()
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }
  }

  const logout = async () => {
    const localPreferenceStore = useLocalPreferenceStore()
    clearLocalStorageItems()
    await localPreferenceStore.clearAll()
    await removeTokenWithExpiryDate()
    currentEntity = null
    isLoggedIn = false
    lastFetchedToken = null
  }

  const getEffectiveExpiryDate = (expiresAt: Date): Date => {
    const serverExpiryDate = new Date(Date.now() + AUTH_TOKEN_EFFECTIVE_LIFETIME_MINUTES * 60 * 1000)
    const claimedExpiryDate = expiresAt ? new Date(expiresAt) : null
    if(!claimedExpiryDate || isNaN(claimedExpiryDate.getTime())) {
      return serverExpiryDate
    }
    return claimedExpiryDate < serverExpiryDate ? claimedExpiryDate : serverExpiryDate
  }

  const setTokenWithExpiryDate = async (token: string, expiresAt: Date): Promise<void> => {
    await setItem('auth-token', token)
    await setItem('auth-expires-at', getEffectiveExpiryDate(expiresAt).toISOString())
  }

  const getTokenExpiryDate = async (): Promise<Date> => {
    const storedExpiryDate = await getItem('auth-expires-at')
    if(typeof storedExpiryDate !== 'string') {
      return null
    }
    const expiryDate = new Date(storedExpiryDate)
    if(isNaN(expiryDate.getTime())) {
      return null
    }
    return expiryDate
  }

  const isTokenRenewalNeeded = async (): Promise<boolean> => {
    const token = await getItem('auth-token')
    if(!token) {
      return false
    }
    const expiryDate = await getTokenExpiryDate()
    if(!expiryDate) {
      return true
    }
    return expiryDate.getTime() - Date.now() <= AUTH_TOKEN_RENEWAL_MARGIN_SECONDS * 1000
  }

  const getToken = async (): Promise<string> => {
    return await getItem<string>('auth-token')
  }

  const removeTokenWithExpiryDate = async () => {
    await removeItem('auth-token')
    await removeItem('auth-expires-at')
  }

  const clearLocalStorageItems = () => {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(`${LocalStorageVariablesType.CollapsedGroupIds}-`)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.push(LocalStorageVariablesType.HiddenDatabasePropertiesExpanded)
    keysToRemove.forEach(key => localStorage.removeItem(key))
    localStorage.removeItem(LocalStorageKeyType.WhatsNewDismissedVersion)
  }

  const currentEntityId = $computed(() => {
    return currentEntity?.entityId
  })

  const currentEntityType = $computed(() => {
    return currentEntity?.entityType
  })

  const hasAuthToken = $computed(() => {
    return isLoggedIn
  })

  const currentUser = $computed<IUserResponseInstance | null>(() => {
    if(currentEntity?.entityType !== EntityType.User) {
      return null
    }
    return (currentEntity.payload as IUserResponseInstance) ?? null
  })

  function setActiveDatabaseTimeTrackerItem(
    item: IDatabaseTimeTrackerItemResponseInstance | null,
  ) {
    if(currentEntity?.entityType !== EntityType.User) {
      return
    }
    currentEntity = {
      ...currentEntity,
      payload: {
        ...currentEntity.payload,
        activeDatabaseTimeTrackerItem: item,
      },
    }
  }

  function patchActiveDatabaseTimeTrackerItem(
    partial: Partial<IDatabaseTimeTrackerItemResponseInstance>,
    expectedId?: string,
  ) {
    if(currentEntity?.entityType !== EntityType.User) {
      return
    }
    const userPayload = currentEntity.payload as IUserResponseInstance
    const currentTracker = userPayload?.activeDatabaseTimeTrackerItem
    if(!currentTracker?.id) {
      return
    }
    if(expectedId && currentTracker.id !== expectedId) {
      return
    }
    currentEntity = {
      ...currentEntity,
      payload: {
        ...userPayload,
        activeDatabaseTimeTrackerItem: {
          ...currentTracker,
          ...partial,
        },
      },
    }
  }

  return $$({
    isLoggedIn,
    currentEntity,
    currentEntityId,
    currentEntityType,
    currentUser,
    isRenewingToken,
    currentLinkId,
    hasAuthToken,
    renewToken,
    renewTokenIfNeeded,
    isTokenRenewalNeeded,
    getTokenExpiryDate,
    initializeAuth,
    refetchCurrentEntity,
    logout,
    setTokenWithExpiryDate,
    getToken,
    removeTokenWithExpiryDate,
    createSession,
    initialize,
    dispose,
    setActiveDatabaseTimeTrackerItem,
    patchActiveDatabaseTimeTrackerItem,
  })
})
